import React, { useEffect, useState, useRef, Suspense } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  Redirect, 
} from "react-router-dom";
import Sidebar from "../components/Sidebar";
import ErrorBoundary from "../components/ErrorBoundary";
import TestModeBanner from "../components/TestModeBanner";
import { isPublicPath } from "../Util/publicPaths";
import { RoleRoute } from "../components/RouteGuards";
// PropertyId import removed — route no longer mounted (was stripping URL).
import PartnersSection from "../components/PartnersSection";
import {
  PATH_ADMIN,
  PATH_DESTINATION_COUNTS,
  PATH_CLIENTS,
  PATH_COLLECTIONS,
  PATH_COLLECTION,
  PATH_FAQ,
  PATH_FAVORITES,
  PATH_FORGOT_PASSWORD,
  PATH_HOME,
  PATH_HOT_DESTINATIONS,
  PATH_HOT_DESTINATION_DETAIL,
  PATH_LOGIN,
  PATH_MAP,
  PATH_PROFILE,
  PATH_LINK,
  PATH_OFFER,
  PATH_PROPERTY_EDIT,
  PATH_RESERVE,
  PATH_RESERVE_CLUSTER,
  PATH_RETURN_FLYWIRE,
  PATH_REPORTS,
  PATH_RESERVATIONS,
  PATH_WHATSNEW,
  PATH_SEARCH,
  PATH_DASHBOARD,
  PATH_SHUB,
  PATH_SIGNUP_THANKS,
  PATH_WISH_LIST,
  PATH_INTOUCH,
  PATH_Welcome,
  PATH_SIGNUP,
  PATH_PROPERTY,
  PATH_CALENDAR,
  PATH_CALENDAR_ID,
  VILLA_PATH_SEARCH,
  PATH_TERMS_CONDITIONS,
  PATH_SEARCH_NEW, //By Jaison
  PATH_APPROVE_AGENCY_REDIRECT,
  PATH_DELETE_AGENCY_REDIRECT,
  PATH_FAVORITES_ADMIN,
  PATH_DEMO_VIDEOS,
  PATH_DATAROOM,
  PATH_GETTING_STARTED,
  PATH_MEDIA_DIRECTORY,
  PATH_MEDIA_DIRECTORY_ADMIN,
  PATH_ENQUIRIES_ADMIN
} from "../Util/constants";
import { baseURL } from "../core";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AuthService from "../services/auth.service";
import { AiOutlineClose } from "react-icons/ai";
import { trackPageView } from '../Util/analytics';
import constants from "../Util/constants";
import ChatIcon from "../components/ChatIcon";
import AppWalkthrough from "../components/AppWalkthrough";
import { setAllActiveCountries, updateSearchValues } from "../store/redux/Property/actions";
import { isAgentValid } from "../Util/general";
import { initPriceDefault } from "../Util/priceLabel";
import { shouldForceOnboarding } from "../Util/onboarding";
import { lazyPage } from "../Util/lazyPage";


// Route pages are code-split with React.lazy so the main bundle no longer ships
// all ~40 pages; they render inside the <Suspense> boundary around <Switch>.
// lazyPage is React.lazy plus one thing: when a page's file is gone because a new version
// was deployed while this tab was open, the tab reloads once instead of showing the error
// screen (../Util/lazyPage.js).
const Auth = lazyPage(() => import("./Auth/Auth"));
const ResetPasswordPage = lazyPage(() => import("./ResetPasswordPage/ResetPasswordPage"));
const SignupThanks = lazyPage(() => import("./SignupThanks"));
const Profile = lazyPage(() => import("./Profile/Profile"));
const Clients = lazyPage(() => import("./Clients"));
const Reservations = lazyPage(() => import("./Reservations"));
const Admin = lazyPage(() => import("./Admin"));
const DestinationCounts = lazyPage(() => import("./DestinationCounts"));
const Welcome = lazyPage(() => import("./Welcome"));
const SearchProperty = lazyPage(() => import("./SearchProperty"));
const AgentDashboard = lazyPage(() => import("./AgentDashboard"));
const Onboarding = lazyPage(() => import("./Onboarding"));
const ShubPanel = lazyPage(() => import("./ShubPanel"));
const Map = lazyPage(() => import("./Map"));
const Reports = lazyPage(() => import("./Reports"));
const Touch = lazyPage(() => import("./Touch"));
const Faq = lazyPage(() => import("./Faq"));
const DemoVideos = lazyPage(() => import("./DemoVideos"));
const HotDestinations = lazyPage(() => import("./HotDestinations"));
const Collections = lazyPage(() => import("./Collections"));
const CollectionSearch = lazyPage(() => import("./Collections/CollectionSearch"));
const WishList = lazyPage(() => import("./WishList"));
const Favorites = lazyPage(() => import("./Favorites"));
const Property = lazyPage(() => import("./Property"));
const Calendar = lazyPage(() => import("./Calendar"));
const Link = lazyPage(() => import("./Link"));
const Offer = lazyPage(() => import("./Offer"));
const CalendarId = lazyPage(() => import("./CalendarId"));
const PropertyEdit = lazyPage(() => import("./PropertyEdit"));
const PropertyReservePage = lazyPage(() => import("./PropertyReserve"));
const ClusterReserve = lazyPage(() => import("./ClusterReserve"));
const TermsConditions = lazyPage(() => import("./termsConditions"));
const Whatsnew = lazyPage(() => import("./Whatsnew"));
const Signup = lazyPage(() => import("./Signup/Signup"));
const VerifyCodePage = lazyPage(() => import("./ResetPasswordPage/VerifyCodePage"));
const ResetChangePasswordPage = lazyPage(() => import("./ResetPasswordPage/ResetChangePasswordPage"));
// Property manager "connect your PMS" page — public, token-gated (no sign-in exists for PMs).
const PmConnect = lazyPage(() => import("./PmConnect"));
const ApproveAgentRedirect = lazyPage(() => import("./ApproveAgentRedirect/ApproveAgentRedirect"));
const DeleteAgentRedirect = lazyPage(() => import("./DeleteAgentRedirect/DeleteAgentRedirect"));
const DataRoom = lazyPage(() => import("./DataRoom"));
const MediaDirectory = lazyPage(() => import("./MediaDirectory"));
const MediaDirectoryAdmin = lazyPage(() => import("./MediaDirectory/Admin"));
const EnquiriesAdmin = lazyPage(() => import("./EnquiriesAdmin"));



export const drawerWidth = 240;

const MainPage = React.memo(function MainPage(props) {
  const loggedRef = useRef(false);
  const hasRedirectedRef = useRef(false); // Track if the redirection has happened

  if (!loggedRef.current) {
    loggedRef.current = true;
  }
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const [token, setToken] = useState(localStorage.getItem("jToken"));
  const [agent, setAgent] = useState(() => {
    const savedAgent = localStorage.getItem("agent");
    return savedAgent ? JSON.parse(savedAgent) : null;
  });

  const [agency, setAgency] = useState(localStorage.getItem("agency"));
  const [currencies, setCurrencies] = useState(localStorage.getItem("currencies"));
  const [currency, setCurrency] = useState(localStorage.getItem("currency") || 'USD');
  const sideBarState = useSelector((state) => state.user.settings.SideBar)
  const [activeMenu, setActiveMenu] = useState(sideBarState);
  const [screenSize, setScreenSize] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [propertyId, setPropertyId] = useState("");
  // const links=localStorage.getItem("noMenu")?true:false;
  const links = location.pathname?.split("/").includes('link');
  // Public, token-gated offer page — chromeless + auth-guard-bypassed, same as /link.
  const offers = location.pathname?.split("/").includes('offer');
  const calendars = location.pathname?.split("/").includes('calendar');
  const [noMenu, setNoMenu] = useState(links||calendars||offers);
  useEffect(() => {
   if(window.innerWidth > 800) {
    setActiveMenu(sideBarState)
   }
    
  }, [sideBarState]); 
  useEffect(() => {
    // Exchange rates are slow-changing: cache in localStorage with a 24h TTL
    // instead of re-fetching /xchange on every load. Bug fix: the old guard read
    // localStorage key "currencies" (never written — the fetch stores "exchange"),
    // so it re-fetched on EVERY load. Also dropped the buggy setItem("currency",
    // 'USD') inside the async fetch, which raced-overwrote the real selection below.
    const CURRENCY_TTL_MS = 24 * 60 * 60 * 1000;
    const exchangeFresh =
      localStorage.getItem("exchange") &&
      Date.now() - Number(localStorage.getItem("exchange_ts") || 0) < CURRENCY_TTL_MS;

    const fetchCurrencies = async () => {
      try {
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        const response = await axios.get(constants.SHUB_URL + "/xchange");
        const data = response.data;
        localStorage.setItem("exchange", JSON.stringify(data));
        localStorage.setItem("exchange_ts", String(Date.now()));
        setCurrencies(data);
      }
      catch (error) {
        console.error("Error fetching currencies:", error);
      }
    }
    if (!exchangeFresh) {
      fetchCurrencies();
    }
    const agentInfo = JSON.parse(localStorage.getItem("agent") || "{}");
    const agentCurrency = agentInfo.currency || "USD";
    setCurrency(currency || agentCurrency || 'USD')
    localStorage.setItem("currency", currency || agentCurrency || 'USD');
  }, [])

  const logintoken = localStorage.getItem("jToken");
  const storedProperty = localStorage.getItem("property") && isValidJSON(localStorage.getItem("property")) ? JSON.parse(localStorage.getItem("property")) : {};

  useEffect(() => {
    getProfile();
    // One-time cache-bust: bump DEST_CACHE_VERSION whenever the destinations set
    // changes shape (e.g. the operated-countries filter) so every client drops its
    // stale localStorage copy and re-fetches once, instead of waiting out the 24h TTL.
    const DEST_CACHE_VERSION = constants.DEST_CACHE_VERSION;
    if (localStorage.getItem('destinations_v') !== DEST_CACHE_VERSION) {
      ['countries', 'regions', 'cities', 'destinations_tree', 'destinations_ts'].forEach((k) => localStorage.removeItem(k));
      localStorage.setItem('destinations_v', DEST_CACHE_VERSION);
    }
    // Destinations change rarely — skip the per-load /local/destinations fetch
    // when the localStorage cache is still warm (<24h). On-demand callers
    // (autocompletes) still fetch normally.
    const DEST_TTL_MS = 24 * 60 * 60 * 1000;
    const destFresh =
      localStorage.getItem("countries") &&
      Date.now() - Number(localStorage.getItem("destinations_ts") || 0) < DEST_TTL_MS;
    if (!destFresh) AuthService.DestinationsOptions();

    dispatch(setAllActiveCountries());

  }, [dispatch]);

  function isValidJSON(str) {
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  }
  useEffect(() => {
    let urls = location.pathname ? location.pathname?.split("/") : [];
    // The pageview send that used to live here has moved out: this effect now runs on
    // every navigation (AUTH-02), and the effect further down already sends one per
    // navigation, so keeping it here would count every pageview twice.

    //console.log(urls)
    if (storedProperty) {
      const { listing, xdata, fullCalendar } = storedProperty;
    }
    // The whole effect used to be wrapped in `if (!links && !offers)`, which skipped it
    // when ANY path segment equalled 'link' or 'offer' — so /calendar/link skipped the
    // guard too. Both are entries in publicPaths.js now, so the bypass is gone and there
    // is genuinely one list.
    if (
      location.pathname !== "/signup" &&
      location.pathname !== "/login" &&
      location.pathname !== "/forgotPassword" &&
      location.pathname !== "/faq" &&
      location.pathname !== "/intouch" &&
      location.pathname !== "/signupthanks"
    ) {

      // Hands the stored property to /reservations through location.state. replace,
      // not push: this runs on arrival, so pushing added a second identical entry and
      // the first Back press appeared to do nothing. Skipped when the state is already
      // there (a normal in-app navigation carries it).
      if (location.pathname === `/reservations` && !location.state?.property) {
        history.replace({
          pathname: `/reservations`,
          state: {
            property: storedProperty?.listing,
            xdata: storedProperty?.xdata,
            fullCalendar: storedProperty?.fullCalendar,
          },
        });
      }
      // AUTH-02: two pushes used to live here, re-pushing the page that was ALREADY
      // being rendered (the conditions were `pathname === pathname`, always true) with
      // the pathname only. That is what dropped ?token= from the reset-password link
      // mailed to users, so the form always loaded with an empty token.
      if (agent && agency && urls[1] === "property") {
        setPropertyId(urls[2]);
        // Do NOT history.push('/property') here — it strips the id from the
        // URL, which destroys the back-button flow from property → search.
        // Property reads its data from location.state, not from the path,
        // so keeping the id in the URL is harmless and required for
        // history.goBack() to return to /search?...
      }
    }
    if (location.pathname === "/") {
      setTimeout(() => { //reset data and move to the login

        localStorage.removeItem("formerDestination");
        // localStorage.removeItem("destination");
        // localStorage.setItem("destination", "");
        localStorage.removeItem("dateFrom");
        localStorage.removeItem("dateTo");
        localStorage.removeItem("adults");
        localStorage.removeItem("children");
        localStorage.removeItem("bedrooms");
        localStorage.removeItem("bathrooms");
        localStorage.removeItem("propertyType");
        localStorage.removeItem("selectedTypes");
        localStorage.removeItem("selectedPrices");
        localStorage.removeItem("selectedMusthave");
        localStorage.removeItem("selectedAmenities");
        localStorage.removeItem("searchedAmenities");
        // A fresh search starts with no Budget bracket ticked, the same as a new browser.
        dispatch(updateSearchValues({
          selectedPrices: initPriceDefault(),
          selectedTypes: [],
          selectedMusthave: [],
          selectedAmenities: [],
        }));
        localStorage.removeItem("selectedPropertiesItem");
        localStorage.removeItem("searchedPriceRange");
        localStorage.removeItem("searchedTypes");
        localStorage.removeItem("searchedMustHave");
        // The Sort By choice is NOT cleared here. It is the agent's preference, not a
        // filter of the last search, and clearing only `sortBy` left its label behind
        // (sortTitle / sortLowToHigh): the control went on showing "Number of BD — Low
        // to High" while the request went out unsorted. It lasts until sign-out.
        localStorage.removeItem("collections");
        localStorage.removeItem("clientEmail");
        // localStorage.removeItem("activeRatePlan");
        history.push("/welcome");

      }, 200);
    }
    if (logintoken === null || !isAgentValid()) {
      // AUTH-02: one list, in src/Util/publicPaths.js. This used to name five prefixes,
      // so a logged-out visitor opening /login, /signup, /forgotPassword, /faq, /intouch
      // or /terms-and-conditions was thrown to /welcome — as was the reset link from
      // their own inbox, whose ?token= the re-push above had already stripped.
      if (!isPublicPath(location.pathname)) {
        history.push({ pathname: "/welcome", search: "" });
      }
    } else if (shouldForceOnboarding(agent) && location.pathname !== PATH_GETTING_STARTED) {
      // First-time agent mid-onboarding: trap them in the chromeless wizard
      // (no sidebar/search) until they finish setup + the walkthrough.
      history.push(PATH_GETTING_STARTED);
    }

    // AUTH-02: this was [] — the guard ran once, on mount, and never again. A logged-out
    // tab could then navigate anywhere for the rest of its life. Keyed on the pathname so
    // it re-checks on every navigation (and only then: pushing the same path is a no-op).
  }, [location.pathname]);

  // UX-12: one page view per route, the first load included (GA is set up in
  // Util/analytics.js with its automatic page_view off).
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => setScreenSize(window.innerWidth);
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize); // Cleanup listener
  }, []);

  useEffect(() => {
    if (screenSize < 800) {
      setActiveMenu(false);
      localStorage.setItem("screen", activeMenu);
      localStorage.setItem("screenSize", screenSize);
    } else {
      setActiveMenu(sideBarState);
      
      localStorage.setItem("screen", activeMenu);
      localStorage.setItem("screenSize", screenSize);
    }
  }, [screenSize]);

  useEffect(() => {
    // A genuine Flywire return carries ?confirmation=... and MUST reach the
    // return handler in PropertyReserve — redirecting it away would silently
    // drop the reservation after the guest paid. Only bare visits (no
    // confirmation param) bounce back to the reserve form.
    const isFlywireReturn = new URLSearchParams(location.search).has("confirmation");
    if (location.pathname === "/request-to-book-flywire" && !isFlywireReturn && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true; // Set to true to avoid infinite loop
      history.replace(PATH_RESERVE);
    }
  }, [location.pathname, location.search, history]);

  const handleToggleMenu = () => {
    if (screenSize < 900) {
      setActiveMenu((preValue) => !preValue);
    } else {
      setActiveMenu(false);
    }
    setIsMenuOpen(true);
  };

  const handleClosedMenu = () => {
    if (screenSize < 900) {
      setActiveMenu((preValue) => !preValue);
    } else {
      setActiveMenu(false);
    }
    setIsMenuOpen(false);
  };


  const userRequest = axios.create({
    baseURL: baseURL,
    headers: {
      token: `Bearer ${localStorage.getItem("jToken")}`,
    },
  });

  const getProfile = async () => {
    if (localStorage.getItem("agent_id")) {
      const agentId = localStorage.getItem("agent_id");
      const res = await userRequest.get(`/agent/get-profile`, {
        params: {
          agent_id: agentId,
        },
      });
      if (res.data?.agent) {
        localStorage.setItem("agent", JSON.stringify(res.data.agent));
        localStorage.setItem(
          "agent_id",
          JSON.stringify(res.data.agent.agent_id)
        );
        localStorage.setItem(
          "agency_id",
          JSON.stringify(res.data.agent.agency_id)
        );
        setAgent(res.data.agent);
        const agencyID = parseInt(res.data.agent.agency_id);

        const TAres = await userRequest.get(
          `travel-agency/get-travel-agencies`,
          {
            params: {
              agency_id: agencyID,
            },
          }
        );
        if (TAres.data?.agencies.length > 0) {
          localStorage.setItem(
            "travelAgency",
            JSON.stringify(TAres.data?.agencies[0]?.agencyDetails[0])
          );
          setAgency(TAres.data?.agencies[0]?.agencyDetails[0]);
        } else {
          console.log(
            "could not read the agency data? agency_id=",
            agent.agency_id
          );
        }
      } else {
        console.log(
          "could not read the agent data? agent_id=",
          agent.agency_id
        );
      }
    }
  };

  const isCompany = localStorage.getItem('currentCompany')

  return (
    <>
      {/* SEC-05: warns on the payment pages whenever Flywire is pointed at the sandbox
          rather than the live environment. Renders nothing when it is live, and nothing
          on any other page — it must never appear on the /link and /offer pages agents
          send to their clients. */}
      <TestModeBanner />
      <ErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<div className="route-loading" style={{ padding: "2rem", textAlign: "center" }}>Loading…</div>}>
      <Switch>
        <Route exact path={[PATH_LOGIN]}>
          <Auth stage="login" signup={true} setToken={setToken} />
        </Route>
        <Route exact path={"/verifycode/:id"}>
          <VerifyCodePage />
        </Route>
        <Route exact path={"/resetpassword/:id"}>
          <ResetChangePasswordPage />
        </Route>
        <Route exact path={"/pm-connect/:token"}>
          <PmConnect />
        </Route>
        <Route exact path={[PATH_FORGOT_PASSWORD]}>
          <ResetPasswordPage />
        </Route>
        <Route exact path={[PATH_SIGNUP_THANKS]}>
          <SignupThanks />
        </Route>
        <Route path={[PATH_Welcome]}>
          <Welcome signup={true} />
        </Route>
        <Route path={[PATH_SIGNUP]}>
          <Signup />
        </Route>
        <Route exact path={[PATH_HOT_DESTINATIONS]}>
          {logintoken !== null && !noMenu && (
            <div className="sidebar">
              <Sidebar
                agent={agent}
                agency={agency}
                screenSize={screenSize}
                activeMenu={activeMenu}
                handleToggleMenu={handleToggleMenu}
              />
            </div>
          )}
          {/* Same content-shift other pages use: when the menu is open (logged in),
              the page resizes right so the sidebar sits beside it. */}
          <div className={activeMenu && logintoken !== null && !noMenu ? "page-body" : "page-body-sm"}>
            <HotDestinations token={token} agent={agent} agency={agency} />
          </div>
        </Route>
        <Route exact path={[PATH_HOT_DESTINATION_DETAIL]}>
          {logintoken !== null && !noMenu && (
            <div className="sidebar">
              <Sidebar
                agent={agent}
                agency={agency}
                screenSize={screenSize}
                activeMenu={activeMenu}
                handleToggleMenu={handleToggleMenu}
              />
            </div>
          )}
          <div className={activeMenu && logintoken !== null && !noMenu ? "page-body" : "page-body-sm"}>
            <HotDestinations token={token} agent={agent} agency={agency} />
          </div>
        </Route>
        <Route exact path={[PATH_DEMO_VIDEOS]}>
          <DemoVideos />
        </Route>

        <Route exact path={[PATH_DATAROOM]}>
          <DataRoom />
        </Route>

        {/* Chromeless: no sidebar / no "What's New" header so first-time agents
            can't navigate away before completing setup + the walkthrough. */}
        <Route exact path={[PATH_GETTING_STARTED]}>
          <Onboarding agent={agent} agency={agency} setAgent={setAgent} setAgency={setAgency} />
        </Route>

        <div className={`page-container ${activeMenu ? "active-menu" : ""}`}>
          {          <div
            style={{
              display: "flex",
              flexDirection: "row",
            }}
            className="page-header"
          >
            {activeMenu && isMenuOpen && !noMenu && (
              <div
                style={{ display: "flex", flex: "2", cursor: "pointer" }}
                onClick={handleClosedMenu}
                className="closed-icon h-100"
              >
                <AiOutlineClose color="#fff" size={30} style={{ top: "2px" }} />
              </div>
            )}

            {!noMenu && location.pathname !== "/whatsnew" && (
              <span
                data-tour-id="whats-new"
                style={
                  activeMenu && isMenuOpen
                    ? { display: "flex", flex: "4", alignItems: "center", fontSize: "18px" }
                    : { display: "flex", justifyContentL: "center", fontSize: "18px" }
                }
                onClick={() => history.push("/whatsnew")}
              >
                <span style={{ cursor: 'pointer' }}>
                  {
                    isCompany ? "What's New on American Express ?" : "What's New on Villatracker ?"
                  }
                </span>
              </span>
            )}
          </div>}
          {logintoken !== null && !noMenu && (<div className="sidebar">
            <Sidebar
              agent={agent}
              agency={agency}
              screenSize={screenSize}
              activeMenu={activeMenu}
              handleToggleMenu={handleToggleMenu}
            />
          </div>)}

          <div
            className={activeMenu && !noMenu ? `${"page-body"}` : "page-body-sm"}
            onClick={() =>
              screenSize < 768 && setActiveMenu((preValue) => false)
            }
          >
            <Route exact path={[PATH_WHATSNEW]}>
              <Whatsnew agency={agency} agent={agent} token={token} />
              
            </Route>
            <Route exact path={[PATH_SEARCH]}>
              <SearchProperty agency={agency} agent={agent} token={token} noMenu={noMenu} />

            </Route>
            <Route exact path={[PATH_DASHBOARD]}>
              <AgentDashboard agency={agency} agent={agent} token={token} />
            </Route>
            <Route exact path={[VILLA_PATH_SEARCH]}>
              <SearchProperty token={token} agent={agent} agency={agency} />
            </Route>
            {/* AUTH-08: the SHub panel, the all-agents favourites and the /searchnew test
                search were open to any logged-in role. Only staff use them. */}
            <RoleRoute exact path={[PATH_SHUB]} role={["admin", "tal"]}>
              <ShubPanel token={token} agent={agent} agency={agency} />
            </RoleRoute>
            <Route exact path={[PATH_MAP]}>
              {/* <Map token={token} agent={agent} agency={agency} /> */}
              <SearchProperty agency={agency} agent={agent} token={token} noMenu={noMenu} />
            </Route>
            <Route exact path={[PATH_CLIENTS]}>
              <Clients token={token} agent={agent} agency={agency} />
            </Route>
            <Route exact path={[PATH_COLLECTIONS]}>
              <Collections
                signup={false}
                token={token}
                agent={agent}
                agency={agency}
              />
            </Route>
            <Route exact path={[PATH_COLLECTION]}>
              <CollectionSearch agency={agency} agent={agent} token={token} noMenu={noMenu} />
            </Route>
            {/* HotDestinations moved to public routes above */}
            <Route exact path={[PATH_FAVORITES]}>
              <Favorites token={token} agent={agent} agency={agency} />
            </Route>
            <RoleRoute exact path={[PATH_FAVORITES_ADMIN]}>
              <Favorites token={token} agent={agent} agency={agency} />
            </RoleRoute>
            {/* Admin only — Praveen, 29 Sep 2026. requireAdmin on the API enforces the
                same thing server-side; this guard only decides what renders. */}
            <RoleRoute exact path={[PATH_ENQUIRIES_ADMIN]}>
              <EnquiriesAdmin />
            </RoleRoute>
            <Route exact path={[PATH_RESERVATIONS]}>
              <Reservations token={token} agent={agent} agency={agency} />
            </Route>
            <Route exact path={[PATH_REPORTS]}>
              <Reports token={token} agent={agent} agency={agency} />
            </Route>
            <Route exact path={[PATH_PROFILE]}>
              <Profile
                agency={agency}
                agent={agent}
                token={token}
                setAgent={setAgent}
                setAgency={setAgency}
              />
            </Route>
            <Route exact path={[PATH_WISH_LIST]}>
              <WishList agency={agency} agent={agent} token={token} />
            </Route>
            <Route exact path={[PATH_INTOUCH]}>
              <Touch token={token} agent={agent} agency={agency} />
            </Route>
            <Route path={PATH_CALENDAR_ID}>
              <CalendarId currency={currency} agency={agency} agent={agent} token={token} noMenu={noMenu} />
            </Route>
            <Route path={PATH_PROPERTY}>
              <Property currency={currency} agency={agency} agent={agent} token={token} noMenu={noMenu} />
            </Route>
            <Route path={PATH_LINK}>
              <Link
                noMenu={noMenu}
                currency={currency}
                agency={agency}
                agent={agent}
                getProfile={getProfile}
                token={token} />
            </Route>
            <Route path={PATH_OFFER}>
              <Offer />
            </Route>
              {/*
                PropertyId Route removed — these inner Routes are NOT inside a
                <Switch>, so PATH_PROPERTY_ID was rendering alongside the
                <Route path={PATH_PROPERTY}> above, and its post-fetch
                history.push({pathname: PATH_PROPERTY, state}) was stripping
                the id from the URL and breaking back navigation. Property at
                line 528 (non-exact path) already matches /property/<id> and
                renders correctly from location.state set by PropertyBox.
              */}
              <Route exact path={PATH_APPROVE_AGENCY_REDIRECT}>
                <ApproveAgentRedirect />
              </Route>
              <Route exact path={PATH_DELETE_AGENCY_REDIRECT}>
                <DeleteAgentRedirect />
              </Route>
              <Route exact path={PATH_CALENDAR}>
              <Calendar
                currency={currency}
                agency={agency}
                agent={agent}
                token={token}
                id={propertyId}
                noMenu={noMenu}
              />
            </Route>
            {/* AUTH-01/AUTH-08: this page writes the listing's xdata to the hub, so it
                is admin-only. It was open to any logged-in role, and reachable logged
                out (the old guard exempted anything starting /property). */}
            <RoleRoute exact path={[PATH_PROPERTY_EDIT]}>
              <PropertyEdit
                propertyId={propertyId}
                agency={agency}
                agent={agent}
                token={token}
              />
            </RoleRoute>
            <Route exact path={[PATH_FAQ]}>
              <Faq />
            </Route>
            <RoleRoute exact path={[PATH_ADMIN]}>
              <Admin agency={agency} agent={agent} token={token} />
            </RoleRoute>
            <RoleRoute exact path={[PATH_DESTINATION_COUNTS]}>
              <DestinationCounts agency={agency} agent={agent} token={token} />
            </RoleRoute>
            <Route exact path={[PATH_MEDIA_DIRECTORY]}>
              <MediaDirectory agency={agency} agent={agent} token={token} />
            </Route>
            <RoleRoute exact path={[PATH_MEDIA_DIRECTORY_ADMIN]}>
              <MediaDirectoryAdmin agency={agency} agent={agent} token={token} />
            </RoleRoute>
            <Route exact path={[PATH_RESERVE]}>
              <PropertyReservePage
                agency={agency}
                agent={agent}
                token={token}
              />
            </Route>
            <Route exact path={[PATH_RESERVE_CLUSTER]}>
              <ClusterReserve />
            </Route>
            <Route exact path={[PATH_RETURN_FLYWIRE]}>
              <PropertyReservePage
                agency={agency}
                agent={agent}
                token={token}
              />
            </Route>
            <Route exact path={[PATH_TERMS_CONDITIONS]}>
              <TermsConditions
                agency={agency}
                agent={agent}
                token={token}
              />
            </Route>
            <RoleRoute exact path={[PATH_SEARCH_NEW]}>
              <SearchProperty agency={agency} agent={agent} token={token} noMenu={noMenu} />
            </RoleRoute>  
            {/* hiding partner section */}
            {/* <PartnersSection />           */}
          </div>
        </div>
      </Switch>
      </Suspense>
      </ErrorBoundary>
      <ChatIcon />
      {/* Spotlight walkthrough — fires once for first-time agents who have
          completed the chromeless onboarding. Self-gates via
          agent.onboarding.walkthroughSeenAt (+ localStorage mirror), so it
          renders cheaply (null) for everyone who has already seen it. */}
      {logintoken !== null && !noMenu && <AppWalkthrough />}
      <ToastContainer />
    </>
  );
})
export default MainPage;
